import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useLazyProducts } from "../../products/hooks";
import { useLazyScansByProduct } from "../../scans/hooks/useScansByProduct";
import { X } from "lucide-react";
import { useDebounce } from "../../../hooks/useDebounce";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Line } from "react-chartjs-2";
import { Loading } from "../../../components";
import { Card } from "../../../components/cards";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

// Couleurs pour différencier les produits
const CHART_COLORS = [
  { border: "rgb(249, 115, 22)", background: "rgba(249, 115, 22, 0.1)" }, // Orange
  { border: "rgb(59, 130, 246)", background: "rgba(59, 130, 246, 0.1)" }, // Blue
  { border: "rgb(16, 185, 129)", background: "rgba(16, 185, 129, 0.1)" }, // Green
  { border: "rgb(236, 72, 153)", background: "rgba(236, 72, 153, 0.1)" }, // Pink
  { border: "rgb(139, 92, 246)", background: "rgba(139, 92, 246, 0.1)" }, // Purple
  { border: "rgb(245, 158, 11)", background: "rgba(245, 158, 11, 0.1)" }, // Amber
  { border: "rgb(20, 184, 166)", background: "rgba(20, 184, 166, 0.1)" }, // Teal
  { border: "rgb(239, 68, 68)", background: "rgba(239, 68, 68, 0.1)" }, // Red
];

interface SelectedProduct {
  id: number;
  name: string;
  siteName?: string;
}

export const Compare: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedProducts, setSelectedProducts] = useState<SelectedProduct[]>(
    []
  );
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [fetchScans] = useLazyScansByProduct();
  const [allScansData, setAllScansData] = useState<any[]>([]);
  const [loadingScans, setLoadingScans] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const [searchProducts, { data, loading, error }] = useLazyProducts();

  console.log({ data, error });
  // Précharger les produits depuis l'URL au montage du composant
  useEffect(() => {
    const productIds = searchParams.get("products");

    if (productIds) {
      const ids = productIds
        .split(",")
        .map((id) => parseInt(id.trim()))
        .filter((id) => !isNaN(id));

      console.log("ids", ids);

      if (ids.length > 0) {
        try {
          searchProducts({
            variables: {
              limit: 100,
              offset: 0,
              where: {
                ids,
              },
            },
          }).then((result) => {
            if (result.data?.products?.results) {
              const products = result.data.products.results.map((p) => ({
                id: p.id,
                name: p.name,
                siteName: p.site?.name,
              }));
              setSelectedProducts(products);
            }
          });
        } catch (error) {
          console.error("Erreur lors du chargement des produits:", error);
        }
        // Charger les produits depuis les IDs
      }
    }
    setIsInitialLoad(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Recherche de produits
  useEffect(() => {
    if (debouncedSearchTerm && debouncedSearchTerm.trim() !== "") {
      setIsSearchOpen(true);
      searchProducts({
        variables: {
          limit: 25,
          offset: 0,
          where: {
            search: debouncedSearchTerm,
          },
        },
      });
    } else {
      setIsSearchOpen(false);
    }
  }, [debouncedSearchTerm]);

  // Charger les scans pour tous les produits sélectionnés
  useEffect(() => {
    const fetchAllProductScans = async () => {
      if (!selectedProducts || selectedProducts.length === 0) {
        setAllScansData([]);
        setLoadingScans(false);
        return;
      }

      setLoadingScans(true);

      try {
        const scansPromises = selectedProducts.map((product) =>
          fetchScans({
            variables: {
              productId: product.id,
              where: {},
              groupBy: "day",
            },
          })
        );

        const results = await Promise.all(scansPromises);

        const processedData = results.map((result, index) => ({
          productName: selectedProducts[index]?.name || "Produit inconnu",
          scans: result.data?.scansByProduct?.results || [],
        }));

        setAllScansData(processedData);
      } catch (error) {
        console.error("Erreur lors du chargement des scans:", error);
      } finally {
        setLoadingScans(false);
      }
    };

    fetchAllProductScans();
  }, [selectedProducts, fetchScans]);

  // Mettre à jour l'URL quand les produits sélectionnés changent
  useEffect(() => {
    // Ne pas mettre à jour l'URL lors du chargement initial
    if (isInitialLoad) return;

    if (selectedProducts.length > 0) {
      const ids = selectedProducts.map((p) => p.id).join(",");
      // Utiliser replace: true pour ne pas ajouter d'entrée dans l'historique
      setSearchParams({ products: ids }, { replace: true });
    } else {
      setSearchParams({}, { replace: true });
    }
  }, [selectedProducts, setSearchParams, isInitialLoad]);

  const handleSelectProduct = (product: any) => {
    if (!selectedProducts.find((p) => p.id === product.id)) {
      setSelectedProducts([
        ...selectedProducts,
        { id: product.id, name: product.name, siteName: product.site?.name },
      ]);
    }
    setSearchTerm("");
    setIsSearchOpen(false);
  };

  const handleRemoveProduct = (productId: number) => {
    setSelectedProducts(selectedProducts.filter((p) => p.id !== productId));
  };

  // Préparer les données pour les graphiques
  const prepareChartData = (dataKey: "price" | "quantity" | "sales") => {
    if (!allScansData || allScansData.length === 0) {
      return { labels: [], datasets: [] };
    }

    // Récupérer toutes les dates uniques et les trier
    const allDates = new Set<string>();
    allScansData.forEach((productData) => {
      productData.scans.forEach((scan: any) => {
        allDates.add(scan.createdAt);
      });
    });

    const sortedDates = Array.from(allDates).sort(
      (a, b) => new Date(a).getTime() - new Date(b).getTime()
    );

    // Formater les labels de dates
    const labels = sortedDates.map((dateStr) => {
      const date = new Date(dateStr);
      const joursSemaine = ["dim", "lun", "mar", "mer", "jeu", "ven", "sam"];
      const jourSemaine = joursSemaine[date.getDay()];
      const jour = date.getDate().toString().padStart(2, "0");
      const mois = (date.getMonth() + 1).toString().padStart(2, "0");
      return `${jourSemaine} ${jour}/${mois}`;
    });

    // Créer un dataset pour chaque produit
    const datasets = allScansData.map((productData, index) => {
      const colorIndex = index % CHART_COLORS.length;
      const color = CHART_COLORS[colorIndex];

      // Créer une map des valeurs par date
      const valueByDate = new Map<string, number>();
      productData.scans.forEach((scan: any) => {
        valueByDate.set(scan.createdAt, scan[dataKey]);
      });

      // Créer les données dans l'ordre des dates triées
      const data = sortedDates.map((date) => valueByDate.get(date) || null);

      return {
        label: productData.productName,
        data: data,
        borderColor: color.border,
        backgroundColor: color.background,
        tension: 0.4,
        borderWidth: 2,
        pointRadius: 4,
        pointBackgroundColor: color.border,
        pointBorderColor: "#fff",
        pointBorderWidth: 2,
        pointHoverRadius: 6,
        pointHoverBackgroundColor: color.border,
        pointHoverBorderColor: "#fff",
        fill: true,
        spanGaps: true,
      };
    });

    return { labels, datasets };
  };

  const chartOptions = (title: string, unit: string) => ({
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: {
        display: true,
        position: "top" as const,
        labels: {
          color: "#374151",
          padding: 15,
          font: {
            size: 11,
            weight: 500,
          },
          usePointStyle: true,
          pointStyle: "circle",
        },
      },
      title: {
        display: true,
        text: title,
        color: "#1F2937",
        font: {
          size: 14,
          weight: 600,
        },
        padding: {
          bottom: 20,
        },
      },
      tooltip: {
        backgroundColor: "rgba(30, 41, 59, 0.95)",
        padding: 12,
        borderColor: "rgba(51, 65, 85, 1)",
        borderWidth: 1,
        titleColor: "#fff",
        bodyColor: "#fff",
        cornerRadius: 8,
        callbacks: {
          label: function (context: any) {
            let label = context.dataset.label || "";
            if (label) {
              label += ": ";
            }
            if (context.parsed.y !== null) {
              label += context.parsed.y.toFixed(2) + " " + unit;
            }
            return label;
          },
        },
      },
    },
    interaction: {
      mode: "index" as const,
      intersect: false,
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          color: "#6B7280",
          font: {
            size: 11,
          },
        },
      },
      y: {
        type: "linear" as const,
        display: true,
        position: "left" as const,
        border: {
          display: false,
        },
        grid: {
          color: "rgba(0, 0, 0, 0.05)",
          drawTicks: false,
        },
        ticks: {
          color: "#6B7280",
          font: {
            size: 11,
          },
          padding: 8,
          callback: function (value: any) {
            return Number.isInteger(value) ? value + " " + unit : null;
          },
        },
      },
    },
  });

  return (
    <div className="container mx-auto px-4 py-6">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">
        Comparaison de produits
      </h1>

      {/* Ligne du haut avec recherche et produits sélectionnés */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        {/* Colonne 1 : Recherche de produit */}
        <Card padding="md">
          <div className="flex flex-col">
            <label className="text-sm font-medium text-gray-700 mb-2">
              Rechercher un produit
            </label>
            <input
              type="text"
              className="input"
              placeholder="Nom du produit..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />

            {isSearchOpen && searchTerm !== "" && (
              <div className="mt-2 bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-y-auto">
                {loading && (
                  <div className="p-4 text-center">
                    <p className="text-sm text-gray-500">Chargement...</p>
                  </div>
                )}

                {!loading && data?.products?.results?.length === 0 && (
                  <div className="p-4 text-center">
                    <p className="text-sm text-gray-500">Aucun résultat</p>
                  </div>
                )}

                {!loading &&
                  data?.products?.results &&
                  data.products.results.length > 0 && (
                    <div className="p-2">
                      {data.products.results.map((product) => (
                        <div
                          key={product.id}
                          className="p-3 hover:bg-gray-50 cursor-pointer rounded-md transition-colors"
                          onClick={() => handleSelectProduct(product)}
                        >
                          <p className="text-sm font-medium text-gray-900">
                            {product.name}
                          </p>
                          <p className="text-xs text-gray-500 mt-0.5">
                            {product.site?.name || "Site inconnu"}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
              </div>
            )}
          </div>
        </Card>

        {/* Colonne 2 : Produits sélectionnés */}
        <Card padding="md">
          <div className="flex flex-col">
            <label className="text-sm font-medium text-gray-700 mb-2">
              Produits sélectionnés ({selectedProducts.length})
            </label>
            <div className="space-y-2 min-h-[48px]">
              {selectedProducts.length === 0 ? (
                <p className="text-sm text-gray-500 italic">
                  Aucun produit sélectionné
                </p>
              ) : (
                selectedProducts.map((product) => (
                  <div
                    key={product.id}
                    className="flex items-center justify-between bg-gray-50 px-3 py-2 rounded-md"
                  >
                    <div className="flex-1 min-w-0 mr-2">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {product.name}
                      </p>
                      {product.siteName && (
                        <p className="text-xs text-gray-500 truncate">
                          {product.siteName}
                        </p>
                      )}
                    </div>
                    <button
                      onClick={() => handleRemoveProduct(product.id)}
                      className="text-red-500 hover:text-red-700 transition-colors flex-shrink-0"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        </Card>
      </div>

      {/* Graphiques */}
      {loadingScans && (
        <div className="flex items-center justify-center py-12">
          <Loading />
        </div>
      )}

      {!loadingScans && selectedProducts.length === 0 && (
        <div className="text-center py-12">
          <p className="text-gray-500">
            Sélectionnez des produits pour afficher les graphiques de
            comparaison
          </p>
        </div>
      )}

      {!loadingScans &&
        selectedProducts.length > 0 &&
        allScansData.length > 0 && (
          <div className="space-y-6">
            {/* Graphique des prix */}
            <Card padding="md">
              <Line
                options={chartOptions("Évolution des prix", "€")}
                data={prepareChartData("price")}
              />
            </Card>

            {/* Graphique des stocks */}
            <Card padding="md">
              <Line
                options={chartOptions("Évolution des stocks", "unités")}
                data={prepareChartData("quantity")}
              />
            </Card>

            {/* Graphique des ventes */}
            <Card padding="md">
              <Line
                options={chartOptions("Évolution des ventes", "ventes")}
                data={prepareChartData("sales")}
              />
            </Card>
          </div>
        )}
    </div>
  );
};
